import React, { useState, useMemo } from 'react'
import { api } from '../api'
import { CalculationMethod, PriceRoundingMethod, Tenant, TenantType } from '../types'

interface Props {
  tenants: Tenant[]
  onRefresh: () => void
  onManageTenantAdmins: (tenantId: string) => void
  createModalOpen?: boolean
  onCloseCreateModal?: () => void
}

export function TenantsView({
  tenants,
  onRefresh,
  onManageTenantAdmins,
  createModalOpen = false,
  onCloseCreateModal,
}: Props) {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | TenantType>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')

  // Modals state
  const [isCreateOpen, setIsCreateOpen] = useState(createModalOpen)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)

  // Form State
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [tenantType, setTenantType] = useState<TenantType>('pool')
  const [siloDatabaseUrl, setSiloDatabaseUrl] = useState('')
  const [calcMethod, setCalcMethod] = useState<CalculationMethod>('cost_plus')
  const [roundingMethod, setRoundingMethod] = useState<PriceRoundingMethod>('no_rounding')
  const [logoData, setLogoData] = useState('')
  const [isActive, setIsActive] = useState(true)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Filtered tenants
  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      const matchSearch =
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.slug.toLowerCase().includes(search.toLowerCase())

      const matchType = typeFilter === 'all' || t.tenant_type === typeFilter
      const matchStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && t.is_active) ||
        (statusFilter === 'inactive' && !t.is_active)

      return matchSearch && matchType && matchStatus
    })
  }, [tenants, search, typeFilter, statusFilter])

  const slugify = (text: string) => {
    const trMap: Record<string, string> = {
      ç: 'c',
      ğ: 'g',
      ı: 'i',
      ö: 'o',
      ş: 's',
      ü: 'u',
      Ç: 'c',
      Ğ: 'g',
      İ: 'i',
      Ö: 'o',
      Ş: 's',
      Ü: 'u',
    }
    return text
      .split('')
      .map((c) => trMap[c] || c)
      .join('')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  const handleOpenCreate = () => {
    setName('')
    setSlug('')
    setTenantType('pool')
    setSiloDatabaseUrl('')
    setCalcMethod('cost_plus')
    setRoundingMethod('no_rounding')
    setLogoData('')
    setIsActive(true)
    setError(null)
    setIsCreateOpen(true)
  }

  const handleNameChange = (val: string) => {
    setName(val)
    if (!editingTenant) {
      setSlug(slugify(val))
    }
  }

  const handleOpenEdit = (t: Tenant) => {
    setEditingTenant(t)
    setName(t.name)
    setSlug(t.slug)
    setTenantType(t.tenant_type)
    setSiloDatabaseUrl(t.silo_database_url || '')
    setCalcMethod(t.default_calculation_method)
    setRoundingMethod(t.price_rounding_method || 'no_rounding')
    setLogoData(t.logo_data || '')
    setIsActive(t.is_active)
    setError(null)
  }

  const handleSaveTenant = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('Tenant adı zorunludur.')
      return
    }
    if (!editingTenant && !slug.trim()) {
      setError('Tenant slug alanı zorunludur.')
      return
    }
    if (tenantType === 'silo' && !siloDatabaseUrl.trim()) {
      setError('Silo mimarisi için veritabanı bağlantı adresi (Database URL) zorunludur.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      if (editingTenant) {
        await api.updateTenant(editingTenant.id, {
          name: name.trim(),
          tenant_type: tenantType,
          silo_database_url: tenantType === 'silo' ? siloDatabaseUrl.trim() : null,
          default_calculation_method: calcMethod,
          price_rounding_method: roundingMethod,
          logo_data: logoData.trim() || undefined,
          is_active: isActive,
        })
        setSuccess(`"${name}" tenantı başarıyla güncellendi.`)
        setEditingTenant(null)
      } else {
        await api.createTenant({
          name: name.trim(),
          slug: slug.trim(),
          tenant_type: tenantType,
          silo_database_url: tenantType === 'silo' ? siloDatabaseUrl.trim() : null,
          default_calculation_method: calcMethod,
          price_rounding_method: roundingMethod,
          logo_data: logoData.trim() || undefined,
          is_active: isActive,
        })
        setSuccess(`"${name}" tenantı başarıyla oluşturuldu.`)
        setIsCreateOpen(false)
        if (onCloseCreateModal) onCloseCreateModal()
      }
      onRefresh()
    } catch (err: any) {
      setError(err.message || 'İşlem sırasında bir hata oluştu.')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (t: Tenant) => {
    const actionName = t.is_active ? 'pasife almak' : 'aktife almak'
    if (!window.confirm(`"${t.name}" organizasyonunu ${actionName} istediğinize emin misiniz?`)) {
      return
    }

    try {
      if (t.is_active) {
        await api.deactivateTenant(t.id)
        setSuccess(`"${t.name}" pasife alındı. Bağlı tüm kullanıcıların erişimi engellendi.`)
      } else {
        await api.activateTenant(t.id)
        setSuccess(`"${t.name}" başarıyla aktif edildi.`)
      }
      onRefresh()
    } catch (err: any) {
      alert(err.message || 'Durum değiştirilemedi.')
    }
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="view-title-block">
          <h2>Tenant & Firma Organizasyonları</h2>
          <p>Sistemdeki tüm kiracı organizasyonlarını yönetin, mimari ve varsayılan parametrelerini yapılandırın</p>
        </div>
        <div className="view-actions">
          <button type="button" className="btn btn-primary" onClick={handleOpenCreate}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Yeni Tenant Ekle</span>
          </button>
        </div>
      </div>

      {success && (
        <div className="alert alert-success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{success}</span>
          <button
            type="button"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setSuccess(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Toolbar & Filters */}
      <div className="toolbar-card">
        <div className="search-input-wrapper">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--color-text-dim)' }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Tenant adı veya slug ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              onClick={() => setSearch('')}
            >
              ✕
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
          >
            <option value="all">Tüm Mimariler</option>
            <option value="pool">Pool Mimarisi</option>
            <option value="silo">Silo (İzole DB)</option>
          </select>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="all">Tüm Durumlar</option>
            <option value="active">Yalnızca Aktifler</option>
            <option value="inactive">Yalnızca Pasifler</option>
          </select>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onRefresh}
            title="Listeyi Yenile"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            <span>Yenile</span>
          </button>
        </div>
      </div>

      {/* Tenants Data Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Firma / Tenant Adı</th>
              <th>Slug</th>
              <th>Mimari</th>
              <th>Hesaplama Yöntemi</th>
              <th>Yuvarlama</th>
              <th>Durum</th>
              <th>Kayıt Tarihi</th>
              <th style={{ textAlign: 'right' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <p>Arama kriterlerine uygun tenant bulunamadı.</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredTenants.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {t.logo_data ? (
                        <img
                          src={t.logo_data}
                          alt=""
                          style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 4,
                            backgroundColor: '#e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            color: '#475569',
                          }}
                        >
                          {t.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <span style={{ fontWeight: 600 }}>{t.name}</span>
                    </div>
                  </td>
                  <td>
                    <code>{t.slug}</code>
                  </td>
                  <td>
                    <span className={`badge badge-${t.tenant_type}`}>
                      {t.tenant_type.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {t.default_calculation_method === 'cost_plus'
                      ? 'Maliyet Üzerinden (Cost-Plus)'
                      : 'Satış Fiyatı Üzerinden (Margin)'}
                  </td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    {t.price_rounding_method === 'round_integer'
                      ? 'Tam Sayıya Yuvarla'
                      : t.price_rounding_method === 'round_nearest_5'
                      ? "5'in Katına Yuvarla"
                      : t.price_rounding_method === 'round_nearest_10'
                      ? "10'un Katına Yuvarla"
                      : 'Yuvarlama Yok'}
                  </td>
                  <td>
                    <span className={`badge badge-${t.is_active ? 'active' : 'inactive'}`}>
                      {t.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--color-text-dim)' }}>
                    {t.created_at ? new Date(t.created_at).toLocaleDateString('tr-TR') : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="actions-cell" style={{ justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => onManageTenantAdmins(t.id)}
                        title="Bu tenantın yöneticilerini yönet"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                        </svg>
                        <span>Yöneticiler</span>
                      </button>

                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleOpenEdit(t)}
                        title="Tenantı Düzenle"
                      >
                        Düzenle
                      </button>

                      <button
                        type="button"
                        className={`btn btn-sm ${t.is_active ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => handleToggleActive(t)}
                        title={t.is_active ? 'Tenantı Pasife Al' : 'Tenantı Aktif Et'}
                      >
                        {t.is_active ? 'Durdur' : 'Etkinleştir'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Tenant Modal */}
      {(isCreateOpen || editingTenant) && (
        <div className="modal-backdrop">
          <div className="modal-dialog modal-dialog-wide">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingTenant ? `Tenant Düzenle: ${editingTenant.name}` : 'Yeni Tenant Oluştur'}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => {
                  setIsCreateOpen(false)
                  setEditingTenant(null)
                  if (onCloseCreateModal) onCloseCreateModal()
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveTenant}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label form-label-required">Tenant / Firma Adı</label>
                    <input
                      type="text"
                      className="form-input"
                      value={name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Örn: Ethernet Teknoloji A.Ş."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label form-label-required">Slug (Benzersiz Kod)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="ethernet"
                      disabled={!!editingTenant}
                      required
                    />
                    <span className="form-help">
                      {editingTenant
                        ? 'Slug kimliği oluşturulduktan sonra değiştirilemez.'
                        : 'Subdomain veya başlık yönlendirmesinde kullanılır.'}
                    </span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label form-label-required">Veri Tabanı Mimarisi</label>
                    <select
                      className="form-select"
                      value={tenantType}
                      onChange={(e) => setTenantType(e.target.value as TenantType)}
                    >
                      <option value="pool">Pool (Paylaşımlı DB - Çok Kiracılı RLS)</option>
                      <option value="silo">Silo (İzole Özel PostgreSQL DB)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label form-label-required">Varsayılan Kâr Hesaplama Yöntemi</label>
                    <select
                      className="form-select"
                      value={calcMethod}
                      onChange={(e) => setCalcMethod(e.target.value as CalculationMethod)}
                    >
                      <option value="cost_plus">
                        Maliyet Üzerinden Kâr Hesabı (Cost-Plus Markup) — Fiyat = Maliyet × (1 + Kâr)
                      </option>
                      <option value="margin">
                        Satış Fiyatı Üzerinden Kâr Hesabı (Gross Margin) — Fiyat = Maliyet / (1 - Kâr)
                      </option>
                    </select>
                  </div>
                </div>

                {tenantType === 'silo' && (
                  <div className="form-group">
                    <label className="form-label form-label-required">Silo Veritabanı URL'i</label>
                    <input
                      type="text"
                      className="form-input"
                      value={siloDatabaseUrl}
                      onChange={(e) => setSiloDatabaseUrl(e.target.value)}
                      placeholder="postgresql://user:pass@db-host:5432/silo_tenant_db"
                      required
                    />
                    <span className="form-help">
                      Bu tenant'a ait izole veritabanı bağlantı dizesi (PostgreSQL Connection String).
                    </span>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nihai Satış Fiyatı Yuvarlama Yöntemi</label>
                    <select
                      className="form-select"
                      value={roundingMethod}
                      onChange={(e) => setRoundingMethod(e.target.value as PriceRoundingMethod)}
                    >
                      <option value="no_rounding">Yuvarlama Yapma (Kuruş hassasiyeti korunur: ₺142,34)</option>
                      <option value="round_integer">En Yakın Tam Sayıya Yuvarla (Örn: ₺142,34 ➔ ₺142,00 / ₺142,60 ➔ ₺143,00)</option>
                      <option value="round_nearest_5">En Yakın 5'in Katı Sayıya Yuvarla (Örn: ₺142,34 ➔ ₺140,00 / ₺143,20 ➔ ₺145,00)</option>
                      <option value="round_nearest_10">En Yakın 10'un Katı Sayıya Yuvarla (Örn: ₺143,20 ➔ ₺140,00 / ₺147,80 ➔ ₺150,00)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Firma Logo Verisi / URL (Opsiyonel)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={logoData}
                      onChange={(e) => setLogoData(e.target.value)}
                      placeholder="https://... veya data:image/png;base64,..."
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '4px' }}>
                  <label className="form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <span>Tenant aktif olarak çalışsın (Kullanıcı girişlerine izin ver)</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsCreateOpen(false)
                    setEditingTenant(null)
                    if (onCloseCreateModal) onCloseCreateModal()
                  }}
                  disabled={loading}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Kaydediliyor…' : editingTenant ? 'Değişiklikleri Kaydet' : 'Tenant Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
